import fetchApi from '../util/fetchApi';

// ─── Response types ────────────────────────────────────────────────────────

export interface OcifXlsxGlobalError {
  code: string;
  message: string;
}

export interface OcifXlsxRowError {
  row: number;
  scheduleCode?: string;
  fieldCode?: string;
  error: string;
}

export interface OcifXlsxScheduleResult {
  status: 'success' | 'partial' | 'error' | 'ignored';
  totalRows: number;
  validRows: number;
  errorRows: number;
  errors: OcifXlsxRowError[];
  ignoredReason?: 'unknown-schedule' | 'not-in-reporting-window';
}

export interface OcifXlsxFieldChange {
  fieldCode: string;
  description: string;
  oldValue: string;
  newValue: string;
}

export interface OcifXlsxScheduleReplacement {
  scheduleName: string;
  previousRowCount: number;
  newRowCount: number;
  fieldChanges: OcifXlsxFieldChange[];
}

export interface OcifXlsxReplacementInfo {
  schedulesToReplace: OcifXlsxScheduleReplacement[];
  unchangedSchedules: string[];
}

export interface FinancialXlsxUploadResult {
  status: 'success' | 'partial' | 'error' | 'requiresConfirmation';
  fileName: string;
  totalRows: number;
  validRows: number;
  errorRows: number;
  globalErrors: OcifXlsxGlobalError[];
  rowErrors: OcifXlsxRowError[];
  scheduleResults: Record<string, OcifXlsxScheduleResult>;
  replacementInfo?: OcifXlsxReplacementInfo;
}

export interface InventoryXlsxUploadResult {
  status: 'success' | 'error';
  fileName: string;
  totalRows: number;
  fileId?: string;
  errors: OcifXlsxGlobalError[];
}

export interface OcifInventoryFileSummary {
  scheduleType: string;
  title: string;
  required: boolean;
  fileId: number | null;
  fileName: string | null;
  uploadedAt: string | null;
  uploadedByName: string | null;
  nothingToReport: boolean;
  // Amendments only: the report's state when the amendment was created. Null/false for a report.
  originalFileId: number | null;
  originalFileName: string | null;
  originalNothingToReport: boolean;
  changedInAmendment: boolean;
}

export interface InventoryNothingToReportResult {
  status: 'success' | 'error';
  scheduleType: string;
  nothingToReport: boolean;
  errors: OcifXlsxGlobalError[];
}

export type ConflictChoice = 'calculated' | 'reported';

export interface ConflictResolutionRequestItem {
  key: string;
  choice: ConflictChoice;
}

/** One resolved conflict: where the value lives in the form and the conflict entry's state. */
export interface ResolvedDataConflict {
  key: string;
  scheduleName: string;
  /** RJSF $id of the schedule's SubformField, e.g. "root_tabGroups_ScheduleRI". */
  schedulePrefix: string;
  fieldCode: string;
  /** The schedule's value for the field when it is a number; null when it is text (see textValue) or an unchanged entry's value is missing. */
  value: number | null;
  /** The schedule's value when its formula returns text (RC-LR's week-day dates); otherwise null. */
  textValue: string | null;
  /** True when the entry was already in the requested state (e.g. resolved in another tab); the fields describe the current state. */
  unchanged: boolean;
  /** True when the entry became `false` (resolved with the calculated value). */
  cleared: boolean;
  reported: string | null;
  calculated: string | null;
  resolved: string | null;
}

export interface SkippedDataConflict {
  key: string;
  reason: 'notFound' | 'invalidValue';
}

export interface ResolvedScheduleStatus {
  scheduleName: string;
  schedulePrefix: string;
  uploadStatus: string;
}

export interface ResolveDataConflictsResult {
  status: 'success' | 'error';
  resolved: ResolvedDataConflict[];
  skipped: SkippedDataConflict[];
  scheduleStatuses: ResolvedScheduleStatus[];
}

// ─── Helpers ───────────────────────────────────────────────────────────────

// Passing json: false overrides the api helper's default json: true,
// so we get back the raw Response object from fetchApi.
const RAW = { json: false } as const;

/** Thrown when the server answers with a non-OK status, carrying that status for the caller. */
export class OcifXlsxApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'OcifXlsxApiError';
    this.status = status;
  }
}

async function parseJsonBody<T>(response: Response): Promise<T> {
  const text = await response.text();
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error(`Server returned non-JSON response (HTTP ${response.status}): ${text.slice(0, 200)}`);
  }
}

/**
 * Parses an upload endpoint's result, throwing when the response is not one.
 *
 * The upload endpoints answer with their own result body — status "success" | "error" — and the
 * financial one does so even on HTTP 400. Anything else that is not 2xx never reached that code:
 * ASP.NET's own ProblemDetails (e.g. a malformed multipart body, which Firefox produces when a
 * selected file changed on disk before it was sent). Its `status` is the HTTP code, a number, so
 * reading it as a result made `status === 'error'` false and showed a failed upload as valid.
 */
async function parseUploadResult<T extends { status?: unknown }>(response: Response): Promise<T> {
  const body = await parseJsonBody<T & { title?: string; error?: string }>(response);
  if (!response.ok && typeof body?.status !== 'string') {
    throw new OcifXlsxApiError(response.status, body?.error ?? body?.title ?? `Upload failed (HTTP ${response.status}).`);
  }
  return body;
}

// ─── API ───────────────────────────────────────────────────────────────────

export const OcifXlsxApi = {
  /**
   * Downloads the financial report Excel template (.xlsx) for the given Report entity.
   * Returns a Blob for the caller to trigger a browser download.
   */
  async downloadFinancialTemplate(entityId: number): Promise<Blob> {
    const response: Response = await fetchApi.api.get(
      `ocif-xlsx/template/financial/${entityId}`,
      RAW,
    );
    if (!response.ok) {
      const body = await parseJsonBody<{ error?: string }>(response).catch(() => ({ error: undefined }));
      throw new Error(body?.error ?? `Failed to download financial template (HTTP ${response.status}).`);
    }
    return response.blob();
  },

  /**
   * Uploads a financial report Excel file (.xlsx).
   * Pass confirmReplace=true on a second call after the user confirms the replacement modal.
   *
   * Both success (200) and validation-error (400) responses return a FinancialXlsxUploadResult body.
   * Check result.status to determine outcome.
   */
  async uploadFinancialXlsx(
    entityId: number,
    file: File,
    confirmReplace = false,
  ): Promise<FinancialXlsxUploadResult> {
    const url = `ocif-xlsx/upload/financial/${entityId}${confirmReplace ? '?confirmReplace=true' : ''}`;
    const response: Response = await fetchApi.api.post(url, { ...RAW, formdata: { file } });
    return parseUploadResult<FinancialXlsxUploadResult>(response);
  },

  /**
   * Downloads a static inventory template for the given schedule type.
   * Throws if columns for that schedule type are not yet defined.
   */
  async downloadInventoryTemplate(scheduleType: string): Promise<Blob> {
    const response: Response = await fetchApi.api.get(
      `ocif-xlsx/template/inventory/${scheduleType}`,
      RAW,
    );
    if (!response.ok) {
      const body = await parseJsonBody<{ error?: string }>(response).catch(() => ({ error: undefined }));
      throw new Error(body?.error ?? `Template for '${scheduleType}' is not yet available.`);
    }
    return response.blob();
  },

  /**
   * Uploads an inventory file for a specific schedule type.
   * Any structural validation failure returns a result with status="error".
   */
  async uploadInventoryXlsx(
    entityId: number,
    scheduleType: string,
    file: File,
  ): Promise<InventoryXlsxUploadResult> {
    const response: Response = await fetchApi.api.post(
      `ocif-xlsx/upload/inventory/${entityId}/${scheduleType}`,
      { ...RAW, formdata: { file } },
    );
    return parseUploadResult<InventoryXlsxUploadResult>(response);
  },

  /**
   * Marks (or unmarks) an inventory schedule as "nothing to report", letting the filer proceed
   * without uploading a file for it. Mutually exclusive with an uploaded file — setting it true
   * clears any previously uploaded file's pointer for that schedule, and a subsequent real
   * upload clears the flag again.
   */
  async setInventoryNothingToReport(
    entityId: number,
    scheduleType: string,
    nothingToReport: boolean,
  ): Promise<InventoryNothingToReportResult> {
    const response: Response = await fetchApi.api.post(
      `ocif-xlsx/inventory/nothing-to-report/${entityId}/${scheduleType}`,
      { ...RAW, data: { nothingToReport } },
    );
    return parseJsonBody<InventoryNothingToReportResult>(response);
  },

  /**
   * Resolves calculation conflicts from a financial upload, each with the reported or the
   * calculated value. The server persists the change and returns exactly what it applied.
   */
  async resolveConflicts(entityId: number, resolutions: ConflictResolutionRequestItem[]): Promise<ResolveDataConflictsResult> {
    const response: Response = await fetchApi.api.post(`ocif-xlsx/conflicts/resolve/${entityId}`, { ...RAW, data: { resolutions } });
    if (!response.ok) {
      const body = await parseJsonBody<{ error?: string }>(response).catch(() => ({ error: undefined }));
      throw new OcifXlsxApiError(response.status, body?.error ?? `Failed to resolve conflicts (HTTP ${response.status}).`);
    }
    return parseJsonBody<ResolveDataConflictsResult>(response);
  },

  /**
   * Summarizes every inventory schedule configured for the given report (required or
   * optional) together with the most recently uploaded file for it, if any. Used by the
   * reviewer-facing summary view.
   */
  async getInventoryFileSummaries(entityId: number): Promise<OcifInventoryFileSummary[]> {
    const response: Response = await fetchApi.api.get(`ocif-xlsx/inventory-files/${entityId}`, RAW);
    if (!response.ok) {
      const body = await parseJsonBody<{ error?: string }>(response).catch(() => ({ error: undefined }));
      throw new OcifXlsxApiError(
        response.status,
        body?.error ?? `Failed to load inventory file summaries (HTTP ${response.status}).`,
      );
    }
    return parseJsonBody<OcifInventoryFileSummary[]>(response);
  },

  /**
   * Downloads an uploaded inventory file referenced by the given report or amendment. Used
   * instead of FileApi because that needs a workflow token for the file's owning entity, and
   * an amendment's untouched schedules point at files its report owns.
   */
  async downloadInventoryFile(entityId: number, fileId: number): Promise<Blob> {
    const response: Response = await fetchApi.api.get(`ocif-xlsx/inventory-files/${entityId}/${fileId}`, RAW);
    if (!response.ok) {
      const body = await parseJsonBody<{ error?: string }>(response).catch(() => ({ error: undefined }));
      throw new OcifXlsxApiError(response.status, body?.error ?? `Failed to download file (HTTP ${response.status}).`);
    }
    return response.blob();
  },
};

/** Triggers a browser download from a Blob with the given filename. */
export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
